import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode, type Ref } from "react";
import {
  Badge,
  COST_RANGE_CAPTION,
  InfoTooltip,
  Spinner,
  cn,
  costRange,
  fmtUsd,
  type CostToolEstimateDrug,
  type Drug,
} from "@dearbill/shared";
import { useApi } from "../../auth/useApi";
import { SearchCombobox } from "./SearchCombobox";
import { RecentChips } from "./RecentChips";
import { SectionHeading, type StepStatus } from "./SectionHeading";

/**
 * Drug-list cap, matching the `rxcuis` limit on the server's
 * `CostToolEstimateRequest`. A longer list stops being a 30-second conversation
 * at the counter.
 */
export const MAX_DRUGS = 25;

/** Whole-dollar cost figure, because the counter conversation rounds to the dollar. */
const wholeUsd = (dollars: number): string => fmtUsd(Math.round(dollars));

export interface MedicationsSectionProps {
  orgId: string;
  drugs: Drug[];
  onAdd: (drug: Drug) => void;
  onRemove: (rxcui: string) => void;
  /** Swap a drug in place for a different strength/form of the same drug. */
  onReplace: (oldRxcui: string, newDrug: Drug) => void;
  /** Question 3's status; done once at least one drug is added. */
  status: StepStatus;
  /** Per-rxcui estimate rows once the estimate resolves; empty before. */
  estimateByRxcui: ReadonlyMap<string, CostToolEstimateDrug>;
  /** Partner-set per-fill quantity overrides keyed by rxcui; assumed rows are absent. */
  quantities: Readonly<Record<string, number>>;
  /** Set a per-fill quantity override (`value`) or clear it back to assumed (`null`). */
  onQuantityChange: (rxcui: string, value: number | null) => void;
  /** Dim the money columns while a refresh is in flight (never blank them). */
  refreshing: boolean;
  /** Most-recent-first remembered drugs; already-added ones are hidden from the chips. */
  recentDrugs: Drug[];
  /** Wipes both recents lists, for shared pharmacy terminals. */
  onClearRecents: () => void;
  /** The drug input; the flow focuses it after a plan or drug selection. */
  inputRef?: Ref<HTMLInputElement> | undefined;
}

/** "DrugName (label)", the same title rule as `M3pProjectionCard`. */
function drugTitle(drug: Drug): string {
  const label = drug.label.trim();
  return label ? `${drug.drugName} (${label})` : drug.drugName;
}

/** Plain unit word for the quantity affordance, derived from the drug's form. */
function unitWord(form: string): string {
  const f = form.toLowerCase();
  if (f.includes("tablet")) return "tablets";
  if (f.includes("capsule")) return "capsules";
  if (f.includes("pack")) return "packs";
  return "units";
}

/**
 * The first two rungs of `fill_quantity` in server/app/services/drug_coverage.py,
 * mirrored so a fresh row is never blank: an oral solid is 30 (one a day for a
 * month) and a pack is 1 (a pack is a supply cycle). Anything else stays unknown
 * until the estimate returns, because the later rungs (RxNorm quantity, FDA
 * package size) need server data.
 */
function derivedQuantity(drug: Drug): number | null {
  const f = drug.form.toLowerCase();
  if (f.includes("tablet") || f.includes("capsule")) return 30;
  if (f.includes("pack")) return 1;
  return null;
}

/**
 * The daily patterns a pharmacist thinks in, keyed by the 30-day-fill
 * quantity they imply. Insertion order is the chip order in the quick-pick
 * menu (most common first).
 */
const FREQUENCY_BY_QUANTITY: ReadonlyMap<number, string> = new Map([
  [30, "1 a day"],
  [60, "2 a day"],
  [90, "3 a day"],
  [15, "1 every other day"],
]);

/**
 * Sig-language reading of a per-fill quantity. Oral solids with a recognisable
 * daily pattern lead with the human reading ("1 a day · 30 tablets/fill").
 * Anything else states the count plainly ("3 units per fill", "1 pack per
 * fill"), so no dosing is invented for injectables.
 */
function quantityPhrase(quantity: number, unit: string): string {
  if (unit === "tablets" || unit === "capsules") {
    const frequency = FREQUENCY_BY_QUANTITY.get(quantity);
    if (frequency !== undefined) return `${frequency} · ${quantity} ${unit}/fill`;
  }
  return `${quantity} ${quantity === 1 ? unit.slice(0, -1) : unit} per fill`;
}

/**
 * Question 3, the medications. One drug is a complete input; selecting from the
 * typeahead adds a row at once and clears the input for the next one. Each row
 * grows coverage detail as the estimate lands: the per-fill cost in whole
 * dollars, the tier badge, and prior-auth, step-therapy and quantity-limit
 * badges, each with an `InfoTooltip` one-liner, because these flags are a
 * separate conversation the pharmacist should spot at once. When a row's
 * deductible-met cost (`costMin`) still differs from `cost` after rounding, the
 * cost reads as a range, and the list carries one caption naming what each
 * bound assumes.
 *
 * Each row names its per-fill quantity in sig language, making the engine's
 * silent assumption visible as the daily pattern it implies, because the
 * pharmacist knows the dispensed quantity. `QuantityControl` renders on every
 * row, with or without a plan, a price or coverage. Before an estimate row
 * exists it shows the partner's override or the `derivedQuantity` default.
 *
 * Each row's label line ("5 MG Oral Tablet") opens a `StrengthSwitcher`,
 * because pharmacists switch strengths often and the typeahead offers the
 * choice only once. Picking a variant swaps the drug in place through
 * `onReplace`.
 *
 * "Frequently used" chips under the input add a remembered drug in one tap,
 * without a lookup; chips for drugs already in the list are hidden.
 */
export function MedicationsSection({
  orgId,
  drugs,
  onAdd,
  onRemove,
  onReplace,
  status,
  estimateByRxcui,
  quantities,
  onQuantityChange,
  refreshing,
  recentDrugs,
  onClearRecents,
  inputRef,
}: MedicationsSectionProps): ReactNode {
  const api = useApi();
  const atCap = drugs.length >= MAX_DRUGS;
  // Chips for drugs already in the list would be dead taps, so they are hidden.
  const offeredRecents = recentDrugs.filter((r) => !drugs.some((d) => d.rxcui === r.rxcui));
  // The range caption shows only while some row's cost actually reads as a range.
  const anyRange = drugs.some((d) => {
    const row = estimateByRxcui.get(d.rxcui);
    return row?.covered === true && costRange(row.costMin, row.cost, wholeUsd).ranged;
  });

  const searchDrugs = async (query: string): Promise<Drug[]> => {
    const results = await api<Drug[]>(
      `/api/partner/orgs/${orgId}/cost-tool/drugs?search=${encodeURIComponent(query)}`,
    );
    return results.filter((d) => !drugs.some((added) => added.rxcui === d.rxcui));
  };

  /**
   * Every strength and form variant of `drug`: a search for the drug's own name
   * (the typeahead's substring search), keeping exact-name matches. The current
   * variant stays in the list, and the menu marks it.
   */
  const fetchVariants = async (drug: Drug): Promise<Drug[]> => {
    const results = await api<Drug[]>(
      `/api/partner/orgs/${orgId}/cost-tool/drugs?search=${encodeURIComponent(drug.drugName)}`,
    );
    return results.filter((d) => d.drugName === drug.drugName);
  };

  return (
    <section
      aria-labelledby="cost-tool-drugs-heading"
      className="border-border-strong rounded-2xl border bg-white p-6"
    >
      <SectionHeading id="cost-tool-drugs-heading" step={3} status={status}>
        Which medications is the patient filling?
      </SectionHeading>

      <div className="mt-3">
        <SearchCombobox<Drug>
          label="Add a medication"
          placeholder="Start typing the drug name"
          listboxLabel="Medication suggestions"
          search={searchDrugs}
          onSelect={onAdd}
          getKey={(d) => d.rxcui}
          renderItem={(d) => <span className="truncate">{drugTitle(d)}</span>}
          noMatchesText="No matching medications"
          disabled={atCap}
          hint={atCap ? `You've reached the ${MAX_DRUGS}-medication limit.` : undefined}
          inputRef={inputRef}
        />
      </div>

      {offeredRecents.length > 0 && !atCap && (
        <div className="mt-3">
          <RecentChips
            label="Frequently used"
            items={offeredRecents}
            getKey={(d) => d.rxcui}
            getChipLabel={(d) => `Add ${drugTitle(d)}`}
            renderChip={(d) => <span className="truncate">{drugTitle(d)}</span>}
            onPick={onAdd}
            onClear={onClearRecents}
          />
        </div>
      )}

      {drugs.length > 0 && (
        <>
          <ul className="mt-4 space-y-2">
            {drugs.map((drug) => (
              <DrugRow
                key={drug.rxcui}
                drug={drug}
                row={estimateByRxcui.get(drug.rxcui)}
                override={quantities[drug.rxcui]}
                refreshing={refreshing}
                fetchVariants={fetchVariants}
                onRemove={() => onRemove(drug.rxcui)}
                onReplace={(next) => onReplace(drug.rxcui, next)}
                onQuantityChange={(value) => onQuantityChange(drug.rxcui, value)}
              />
            ))}
          </ul>
          {/* One caption for the whole list names the 30-day assumption behind
              every default and how to correct it. */}
          <p className="text-slate-teal mt-2 text-sm leading-5">
            We assume a 30-day fill. Tap a quantity to match what&rsquo;s actually dispensed —
            e.g.&nbsp;twice-daily means 60 tablets.
          </p>
          {anyRange && (
            <p className="text-slate-teal mt-1 text-sm leading-5">{COST_RANGE_CAPTION}</p>
          )}
        </>
      )}
    </section>
  );
}

function DrugRow({
  drug,
  row,
  override,
  refreshing,
  fetchVariants,
  onRemove,
  onReplace,
  onQuantityChange,
}: {
  drug: Drug;
  row: CostToolEstimateDrug | undefined;
  /** The partner's per-fill quantity override for this drug, if any. */
  override: number | undefined;
  refreshing: boolean;
  fetchVariants: (drug: Drug) => Promise<Drug[]>;
  onRemove: () => void;
  /** Swap this row for a different strength/form of the same drug. */
  onReplace: (next: Drug) => void;
  /** Sets this drug's per-fill quantity override; `null` resets it to assumed. */
  onQuantityChange: (value: number | null) => void;
}): ReactNode {
  // Display priority: the partner's override, then the estimate's quantity
  // (the number that actually priced), then the client-side default (so an
  // oral-solid row is never blank while the estimate is in flight), then
  // genuinely unknown.
  const quantity = override ?? row?.quantity ?? derivedQuantity(drug);
  const assumed = override === undefined && (row?.quantityAssumed ?? true);
  return (
    <li className="border-haze flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border bg-white px-4 py-3">
      {/* The name keeps a width floor so a badge-heavy estimate row can't
          crush it; the badge cluster (zero flex basis) wraps its badges onto
          extra lines instead of squeezing the name out. */}
      <div className="min-w-44 max-w-full">
        <p className="text-foreground truncate text-sm font-semibold">{drug.drugName}</p>
        <StrengthSwitcher drug={drug} fetchVariants={fetchVariants} onReplace={onReplace} />
      </div>

      <div
        className={cn(
          "flex min-w-0 flex-1 basis-0 flex-wrap items-center justify-end gap-2 transition-opacity",
          refreshing && "opacity-50",
        )}
      >
        {row &&
          (row.covered ? (
            <>
              {row.cost !== null && (
                <span className="text-foreground whitespace-nowrap text-sm font-semibold">
                  est. {costRange(row.costMin, row.cost, wholeUsd).text}/fill
                </span>
              )}
              {row.tier !== null && <Badge tone="neutral">Tier {row.tier}</Badge>}
              {row.pa === true && (
                <FlagBadge
                  label="Prior auth"
                  tip="The plan must approve this drug before it's covered."
                />
              )}
              {row.st === true && (
                <FlagBadge label="Step therapy" tip="The plan wants another drug tried first." />
              )}
              {row.ql === true && (
                <FlagBadge
                  label="Quantity limit"
                  tip="The plan caps how much is covered per fill."
                />
              )}
            </>
          ) : (
            <Badge tone="error">Not on this plan&rsquo;s list</Badge>
          ))}
        {/* Always rendered, with or without a plan or coverage, so the
            quantity control works the same everywhere and a pre-estimate
            override flows into the next estimate. */}
        <QuantityControl
          rxcui={drug.rxcui}
          drugName={drug.drugName}
          unit={unitWord(drug.form)}
          quantity={quantity}
          assumed={assumed}
          onChange={onQuantityChange}
        />
      </div>

      <button
        type="button"
        aria-label={`Remove ${drug.drugName}`}
        onClick={onRemove}
        className="text-slate-teal hover:bg-ice hover:text-foreground border-haze inline-flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-md border"
      >
        <svg
          width={16}
          height={16}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.75}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </li>
  );
}

/**
 * The row's label line ("5 MG Oral Tablet") as a strength and form switcher.
 * A tap fetches every same-name variant, through the typeahead's drug search,
 * into a popover listbox. The current variant is checked, and picking another
 * calls `onReplace` and closes. A lone variant renders as a disabled "only
 * strength we have" line, and a fetch failure shows an inline retry. Escape
 * and an outside pointerdown close the popover.
 */
function StrengthSwitcher({
  drug,
  fetchVariants,
  onReplace,
}: {
  drug: Drug;
  fetchVariants: (drug: Drug) => Promise<Drug[]>;
  onReplace: (next: Drug) => void;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [variants, setVariants] = useState<Drug[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  // Stale-response guard: a close, including the one before a replace,
  // invalidates any in-flight fetch so it can't reopen state it no longer owns.
  const fetchSeqRef = useRef(0);

  const load = (): void => {
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    setError(false);
    fetchVariants(drug)
      .then((results) => {
        if (seq !== fetchSeqRef.current) return;
        setVariants(results);
        setLoading(false);
      })
      .catch(() => {
        if (seq !== fetchSeqRef.current) return;
        setLoading(false);
        setError(true);
      });
  };

  const close = (): void => {
    fetchSeqRef.current++;
    setOpen(false);
  };

  // An outside pointerdown closes the popover; the listener is attached only
  // while open. The close logic is inlined, not the `close` helper, so the
  // effect depends on `open` alone.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent): void => {
      if (containerRef.current?.contains(event.target as Node) !== true) {
        fetchSeqRef.current++;
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  const label = drug.label.trim() !== "" ? drug.label : "Choose a strength";

  return (
    <div
      ref={containerRef}
      className="relative"
      onKeyDown={(event) => {
        if (event.key === "Escape" && open) close();
      }}
    >
      <button
        type="button"
        aria-label={`Change the strength or form for ${drug.drugName}`}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => {
          if (open) {
            close();
            return;
          }
          setOpen(true);
          load();
        }}
        className="text-slate-teal hover:text-foreground inline-flex min-h-11 max-w-full cursor-pointer items-center truncate text-xs underline decoration-dotted underline-offset-2"
      >
        {label}
      </button>

      {open && (
        <div className="border-haze absolute left-0 top-full z-20 mt-1 w-max max-w-72 rounded-md border bg-white py-1 shadow-lg">
          {loading ? (
            <div className="flex items-center gap-2 px-4 py-2.5">
              <Spinner size="sm" label="Loading other strengths" />
              <span className="text-slate-teal text-sm">Loading…</span>
            </div>
          ) : error ? (
            <div className="px-4 py-2.5">
              <p className="text-error-red text-sm">Couldn&rsquo;t load other strengths</p>
              <button
                type="button"
                onClick={load}
                className="text-ember hover:text-ember-dark inline-flex min-h-11 cursor-pointer items-center text-sm underline underline-offset-2"
              >
                Try again
              </button>
            </div>
          ) : variants.length <= 1 ? (
            <p className="text-slate-teal px-4 py-2.5 text-sm" aria-disabled="true">
              This is the only strength we have
            </p>
          ) : (
            <div role="listbox" aria-label={`Strengths and forms for ${drug.drugName}`}>
              {variants.map((variant) => {
                const current = variant.rxcui === drug.rxcui;
                return (
                  <button
                    key={variant.rxcui}
                    type="button"
                    role="option"
                    aria-selected={current}
                    onClick={() => {
                      close();
                      if (!current) onReplace(variant);
                    }}
                    className={cn(
                      "hover:bg-ice text-foreground flex min-h-11 w-full cursor-pointer items-center gap-2 px-4 py-2 text-left text-sm",
                      current && "font-semibold",
                    )}
                  >
                    <span className="min-w-0 flex-1 truncate">{variant.label}</span>
                    {current && <CheckMarkIcon size={16} className="text-ember shrink-0" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Check glyph for the current option. It is aria-hidden; the option's
 * aria-selected carries the meaning.
 */
function CheckMarkIcon({ size, className }: { size: number; className?: string }): ReactNode {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="m5 13 4 4L19 7" />
    </svg>
  );
}

/**
 * The per-fill quantity control on every drug row, stating the estimate's
 * assumption in sig language rather than inventory language. An oral solid
 * with a recognisable daily pattern reads "1 a day · 30 tablets/fill ·
 * assumed", or "· set by you" with a Reset once overridden. Other forms state
 * the count plainly ("3 units per fill"), so no dosing is invented for
 * injectables. An unknown quantity renders a "Set quantity per fill" button.
 *
 * A tap on tablets or capsules opens a quick-pick popover of the common daily
 * patterns plus a "Custom…" option that reveals the numeric input. Other forms
 * go straight to the input, because a frequency chip would be a dosing guess.
 * Picking a chip fires `onChange` at once; Escape and an outside pointerdown
 * close the popover. In the input, Enter or blur confirms, Escape cancels, and
 * an empty or unparseable value confirms nothing. A confirmed value is rounded
 * and clamped to the input's `min` and `max`; `CostToolEstimateRequest` rejects
 * anything above that max.
 */
function QuantityControl({
  rxcui,
  drugName,
  unit,
  quantity,
  assumed,
  onChange,
}: {
  rxcui: string;
  drugName: string;
  /** Plain unit word from `unitWord`. */
  unit: string;
  quantity: number | null;
  assumed: boolean;
  onChange: (value: number | null) => void;
}): ReactNode {
  const oralSolid = unit === "tablets" || unit === "capsules";
  const [mode, setMode] = useState<"closed" | "menu" | "input">("closed");
  const containerRef = useRef<HTMLSpanElement | null>(null);
  // Escape closes the editor and must swallow the input blur it triggers;
  // otherwise the blur-confirm would fire the value the user just abandoned.
  // It is a ref, reset on every edit-open, so a swallowed blur can't leak into
  // the next edit session.
  const cancelledRef = useRef(false);

  // An outside pointerdown closes the quick-pick menu; the listener is attached only while open.
  useEffect(() => {
    if (mode !== "menu") return;
    const onPointerDown = (event: PointerEvent): void => {
      if (containerRef.current?.contains(event.target as Node) !== true) setMode("closed");
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [mode]);

  const confirm = (raw: string): void => {
    setMode("closed");
    const value = Number(raw);
    if (raw.trim() === "" || !Number.isFinite(value)) return;
    const clamped = Math.min(5000, Math.max(1, Math.round(value)));
    onChange(clamped);
  };
  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Enter") confirm(event.currentTarget.value);
    if (event.key === "Escape") {
      cancelledRef.current = true;
      setMode("closed");
    }
  };

  if (mode === "input") {
    return (
      <span className="inline-flex items-center gap-1.5">
        <label htmlFor={`cost-tool-qty-${rxcui}`} className="sr-only">
          Quantity per fill for {drugName}
        </label>
        <input
          id={`cost-tool-qty-${rxcui}`}
          type="number"
          min={1}
          max={5000}
          step={1}
          defaultValue={quantity ?? undefined}
          autoFocus
          onKeyDown={onInputKeyDown}
          onBlur={(event) => {
            if (!cancelledRef.current) confirm(event.target.value);
          }}
          className="border-haze text-foreground h-11 w-20 rounded-md border bg-white px-2 text-center text-base"
        />
        <span className="text-slate-teal whitespace-nowrap text-base">{unit} per fill</span>
      </span>
    );
  }

  return (
    <span
      ref={containerRef}
      className="relative inline-flex items-center"
      onKeyDown={(event) => {
        if (event.key === "Escape" && mode === "menu") setMode("closed");
      }}
    >
      <button
        type="button"
        onClick={() => {
          if (mode === "menu") {
            setMode("closed");
            return;
          }
          cancelledRef.current = false;
          setMode(oralSolid ? "menu" : "input");
        }}
        aria-label={
          quantity === null
            ? `Set the quantity per fill for ${drugName}`
            : `Change the quantity per fill for ${drugName}`
        }
        {...(oralSolid ? { "aria-expanded": mode === "menu", "aria-haspopup": true } : {})}
        className={cn(
          "hover:bg-ice inline-flex min-h-11 cursor-pointer items-center whitespace-nowrap rounded-md px-2 text-base underline decoration-dotted underline-offset-2",
          assumed ? "text-slate-teal" : "text-foreground",
        )}
      >
        {quantity === null ? (
          <>Set quantity per fill</>
        ) : assumed ? (
          <>{quantityPhrase(quantity, unit)} · assumed</>
        ) : (
          <>
            {quantityPhrase(quantity, unit)} ·&nbsp;
            <span className="text-ember">set by you</span>
          </>
        )}
      </button>
      {quantity !== null && !assumed && (
        <button
          type="button"
          onClick={() => onChange(null)}
          aria-label={`Reset the quantity per fill for ${drugName}`}
          className="text-slate-teal hover:text-foreground inline-flex min-h-11 cursor-pointer items-center px-2 text-base underline underline-offset-2"
        >
          Reset
        </button>
      )}

      {mode === "menu" && (
        <div
          role="listbox"
          aria-label={`Quantity per fill for ${drugName}`}
          className="border-haze absolute right-0 top-full z-20 mt-1 w-max rounded-md border bg-white py-1 shadow-lg"
        >
          {[...FREQUENCY_BY_QUANTITY].map(([value, frequency]) => {
            const current = value === quantity;
            return (
              <button
                key={value}
                type="button"
                role="option"
                aria-selected={current}
                onClick={() => {
                  setMode("closed");
                  onChange(value);
                }}
                className={cn(
                  "hover:bg-ice text-foreground flex min-h-11 w-full cursor-pointer items-center gap-2 px-4 py-2 text-left text-base",
                  current && "font-semibold",
                )}
              >
                <span className="min-w-0 flex-1">
                  {frequency} ({value})
                </span>
                {current && <CheckMarkIcon size={16} className="text-ember shrink-0" />}
              </button>
            );
          })}
          <button
            type="button"
            role="option"
            aria-selected={false}
            onClick={() => setMode("input")}
            className="hover:bg-ice text-foreground flex min-h-11 w-full cursor-pointer items-center px-4 py-2 text-left text-base"
          >
            Custom…
          </button>
        </div>
      )}
    </span>
  );
}

/** A warning badge with a tooltip one-liner. */
function FlagBadge({ label, tip }: { label: string; tip: string }): ReactNode {
  return (
    <span className="inline-flex items-center gap-1">
      <Badge tone="pending">{label}</Badge>
      <InfoTooltip text={tip} label={`About ${label.toLowerCase()}`} />
    </span>
  );
}
